import React, { useState } from 'react';
import { TaskyBadge, TaskyButton, useConfirm } from '../../../../components/tasky';
import { useToast } from '../../../../contexts/ToastContext';
import { updateOrganization } from '../../../../services/api/adminOrganizationService';

export interface CompetitorMonitoringControlProps {
  organizationId: number;
  /** 停止の打刻。null なら巡回中。 */
  disabledAt: string | null;
  /** 巡回対象（status=active）の監視サイト数。 */
  activeSiteCount: number;
  /** 更新後に組織情報を取り直すためのコールバック。 */
  onUpdated: () => void;
}

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

/**
 * 組織単位の競合監視オプトアウト。
 *
 * 旧表示は `enable_competitor_monitoring` のバッジだったが、スケジューラがこのフラグを
 * 参照しておらず「無効」と出ていても巡回が続いていた。実際に巡回を止める
 * `competitor_monitoring_disabled_at` の打刻をここで操作する。
 */
const CompetitorMonitoringControl: React.FC<CompetitorMonitoringControlProps> = ({
  organizationId,
  disabledAt,
  activeSiteCount,
  onUpdated,
}) => {
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const isDisabled = Boolean(disabledAt);

  const handleToggle = async () => {
    const nextDisabled = !isDisabled;
    const ok = await confirm(
      nextDisabled
        ? {
            title: 'この組織の競合監視を停止します。',
            description: `巡回中の ${activeSiteCount} サイトの自動スクレイピングが止まり、トークンの消費もなくなります。サイトの登録自体は残ります。`,
            tone: 'destructive',
            confirmText: '停止する',
          }
        : {
            title: 'この組織の競合監視を再開します。',
            description: '次回のスケジューラ実行から、登録済みのサイトの自動巡回が再開されます。',
            confirmText: '再開する',
          },
    );
    if (!ok) return;

    setIsSaving(true);
    try {
      await updateOrganization(organizationId, { competitor_monitoring_disabled: nextDisabled });
      showToast(nextDisabled ? '競合監視を停止しました。' : '競合監視を再開しました。', 'success');
      onUpdated();
    } catch (error: any) {
      console.error('Failed to update competitor monitoring:', error);
      showToast(error?.message || '競合監視の設定を更新できませんでした。', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {isDisabled ? (
        <TaskyBadge variant="warning" dot>
          管理者が停止中（{formatDate(disabledAt as string)}）
        </TaskyBadge>
      ) : (
        <TaskyBadge variant="success" dot>
          稼働中（{activeSiteCount} サイト）
        </TaskyBadge>
      )}
      <TaskyButton
        type="button"
        size="sm"
        variant={isDisabled ? 'secondary' : 'destructive'}
        onClick={handleToggle}
        isLoading={isSaving}
      >
        {isDisabled ? '監視を再開' : '監視を停止'}
      </TaskyButton>
    </div>
  );
};

export default CompetitorMonitoringControl;
